import { Copy, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type ComponentProps, type ReactElement } from 'react';
import { Button, Card, Dialog, Flex, IconButton, Theme } from '@/components/common';
import { analyticsClient, profileShareUrl } from '@/lib';

type IconButtonProps = ComponentProps<typeof IconButton>;

interface ShareProfileDialogProps {
  uid: string;
  name: string;
  icon?: ReactElement;
  triggerVariant?: IconButtonProps['variant'];
  triggerRadius?: IconButtonProps['radius'];
}

const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
const canWriteClipboard =
  typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function';

export function ShareProfileDialog({
  uid,
  name,
  icon = <Share2 size={16} aria-hidden />,
  triggerVariant = 'outline',
  triggerRadius = 'full',
}: ShareProfileDialogProps) {
  const { t } = useTranslation('profile');
  const url = profileShareUrl(uid);

  const handleCopy = async () => {
    try {
      if (canWriteClipboard) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for environments without Clipboard API
        const el = document.createElement('textarea');
        el.value = url;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        const success = document.execCommand('copy');
        document.body.removeChild(el);
        if (!success) throw new Error('execCommand copy failed');
      }
      analyticsClient.capture('profile_link_copied');
      toast.success(t('share.copied'));
    } catch (err) {
      console.error('Failed to copy profile link:', err);
      toast.error(t('share.copyFailed'));
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: t('share.shareTitle', { name }),
        text: t('share.shareMessage', { url }),
      });
      // navigator.share resolves when the share sheet is dismissed or a target is chosen;
      // treat resolution as "initiated" rather than "completed".
      analyticsClient.capture('profile_share_initiated');
      toast.success(t('share.shared'));
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Failed to share profile:', err);
        toast.error(t('share.shareFailed'));
      }
    }
  };

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <IconButton
          type="button"
          variant={triggerVariant}
          color="gray"
          highContrast
          radius={triggerRadius}
          size="1"
          aria-label={t('share.triggerAria')}
          data-testid="share-profile-trigger"
        >
          {icon}
        </IconButton>
      </Dialog.Trigger>
      <Dialog.Content size="1">
        <Dialog.Title>{t('share.title')}</Dialog.Title>
        <Flex direction="column" align="center" gap="3" py="2" px="2">
          <Dialog.Description size="1" color="gray" align="center" mb="0">
            {t('share.subtitle')}
          </Dialog.Description>
          {/* QR codes need a light surface to stay scannable in dark theme. */}
          <Theme appearance="light">
            <Card variant="surface" size="2">
              <QRCodeSVG value={url} size={200} />
            </Card>
          </Theme>
          <Flex direction="column" gap="2" mt="1" width="100%">
            {canShare && (
              <Button
                type="button"
                color="gray"
                highContrast
                fullWidth
                prefixIcon={<Share2 />}
                onClick={() => void handleShare()}
                data-testid="share-profile-native"
              >
                {t('share.share')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              color="gray"
              fullWidth
              prefixIcon={<Copy />}
              onClick={() => void handleCopy()}
              data-testid="share-profile-copy"
            >
              {t('share.copyLink')}
            </Button>
            <Dialog.Close>
              <Button type="button" variant="outline" color="gray" fullWidth>
                {t('share.close')}
              </Button>
            </Dialog.Close>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export type { ShareProfileDialogProps };
